// Lightweight agent id+name listing, shared by binding pickers (DEV-16819).
// Shares the ['agents'] query key with routes/agents/index.tsx so both stay
// in sync from one fetch.
import { useClient } from '@cortex/react-query';
import type { AgentListItem } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

export function useAgentList(opts: { includeSystem?: boolean } = {}) {
  const client = useClient();
  const includeSystem = opts.includeSystem ?? false;
  return useQuery({
    // Default (org-only) keeps the ['agents'] key shared with
    // routes/agents/index.tsx; the binding-picker variant additionally pulls
    // the global managed chat/refinement agents (DEV-16995) under its own key.
    queryKey: includeSystem ? ['agents', 'includeSystem'] : ['agents'],
    queryFn: () =>
      client.http
        .get('v1/agents', includeSystem ? { searchParams: { includeSystem: 'true' } } : undefined)
        .json<IListEnvelope<AgentListItem>>(),
    // Keep kind/managed so binding pickers can badge managed system agents
    // (DEV-16995) instead of rendering them indistinguishable from org agents.
    select: (page) =>
      page.items.map((a) => ({ id: a.id, name: a.name, kind: a.kind, managed: a.managed })),
  });
}
